/**
 * Document AI — frontend types (mirror of backend DTOs).
 *
 * The Document AI engine runs OCR + classification + structured field extraction
 * over documents already uploaded via the Document Engine. One document may
 * have multiple extraction runs; the API always returns the latest "current" one.
 */

export type DocAiDocumentType =
  | 'INVOICE'
  | 'AE_REPORT'
  | 'PRESCRIPTION'
  | 'CONSENT_FORM'
  | 'TREATMENT_SUMMARY'
  | 'LAB_REPORT'
  | 'UNKNOWN';

export type DocAiExtractionStatus =
  | 'NOT_PROCESSED'
  | 'PENDING'
  | 'PROCESSING'
  | 'COMPLETED'
  | 'FAILED'
  | 'APPROVED'
  | 'REJECTED';

export type DocAiTagSource = 'AI' | 'USER_OVERRIDE';
export type DocAiConfidenceBand = 'NONE' | 'LOW' | 'MEDIUM' | 'HIGH';

export interface DocAiSuggestedLink {
  caseId?: string | null;
  caseNumber?: string | null;
  cycleId?: string | null;
  cycleNumber?: number | null;
  claimId?: string | null;
  patientId?: string | null;
  linkScore: number;
  reason?: string | null;
}

export interface DocAiExtraction {
  documentAiExtractionId: string;
  entityDocumentId: string;
  programId: string;

  detectedDocumentType: DocAiDocumentType;
  tagSource: DocAiTagSource;
  confidenceScore: number | null;
  confidenceBand: DocAiConfidenceBand;
  extractionStatus: DocAiExtractionStatus;

  extractedFields: Record<string, unknown>;
  suggestedLinks: DocAiSuggestedLink | null;

  ocrEngine: string;
  processedAt: string | null;
  processingDurationMs: number | null;
  errorMessage: string | null;

  approvedByUserId: string | null;
  approvedAt: string | null;
  reviewNotes: string | null;
  runNumber: number;
  createdOn: string;

  fileName: string;
  contentType: string | null;
  fileSizeBytes: number | null;
  uploadedAt: string;
  entityType: string;
  entityId: string;
}

export interface DocAiExtractionListItem {
  entityDocumentId: string;
  fileName: string;
  contentType: string | null;
  fileSizeBytes: number | null;
  uploadedAt: string;
  entityType: string;
  entityId: string;

  documentAiExtractionId: string | null;
  detectedDocumentType: DocAiDocumentType | null;
  tagSource: DocAiTagSource | null;
  confidenceScore: number | null;
  confidenceBand: DocAiConfidenceBand;
  extractionStatus: DocAiExtractionStatus;
  processedAt: string | null;
  fieldCount: number;
  hasSuggestedLinks: boolean;
}

export interface DocAiList {
  items: DocAiExtractionListItem[];
  totalCount: number;
  page: number;
  pageSize: number;
}

export interface DocAiListFilter {
  patientId?: string;
  caseId?: string;
  entityType?: string;
  entityId?: string;
  status?: DocAiExtractionStatus;
  documentType?: DocAiDocumentType;
  page?: number;
  pageSize?: number;
}

export interface DocAiUpdatePayload {
  detectedDocumentType?: DocAiDocumentType;
  extractedFields?: Record<string, unknown>;
  links?: DocAiSuggestedLink;
  reviewNotes?: string;
}

export interface DocAiApprovePayload {
  reviewNotes?: string;
  applyLinks?: boolean;
}

export const DOC_AI_TYPE_LABELS: Record<DocAiDocumentType, string> = {
  INVOICE: 'Invoice',
  AE_REPORT: 'AE Report',
  PRESCRIPTION: 'Prescription',
  CONSENT_FORM: 'Consent Form',
  TREATMENT_SUMMARY: 'Treatment Summary',
  LAB_REPORT: 'Lab Report',
  UNKNOWN: 'Unknown',
};

export const DOC_AI_BAND_COLORS: Record<DocAiConfidenceBand, string> = {
  HIGH: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  MEDIUM: 'bg-amber-100 text-amber-800 border-amber-200',
  LOW: 'bg-red-100 text-red-700 border-red-200',
  NONE: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const DOC_AI_STATUS_COLORS: Record<DocAiExtractionStatus, string> = {
  APPROVED: 'bg-emerald-100 text-emerald-800',
  COMPLETED: 'bg-blue-100 text-blue-800',
  PROCESSING: 'bg-amber-100 text-amber-800',
  PENDING: 'bg-slate-100 text-slate-700',
  FAILED: 'bg-red-100 text-red-700',
  REJECTED: 'bg-red-100 text-red-700',
  NOT_PROCESSED: 'bg-slate-100 text-slate-500',
};
